<template>
  <div class="paymentOption" :class="{ single: singleRateTypeAvailable, active: isSelected }">
    <div class="paymentOptionHeader">
      <BaseFormFieldRadio
        v-if="!singleRateTypeAvailable || (singleRateTypeAvailable && styleOption === 1)"
        :schema="schema.paymentOption"
        :options="[{ label: rateType, value: option }]"
        v-model="ratePlanOption"
        @update:modelValue="handleRadioButtonClick"
        :class="{ active: isSelected }"
      />
    </div>
    <div class="paymentOptionDetails" :class="`styleOption${styleOption}`">
      <div v-if="singleRateTypeAvailable && styleOption === 2" class="rateType">{{ rateType }}</div>
      <div class="ratePercent">{{ option.rateDisplay }}</div>
      <div class="monthlyPaymentAmount" v-if="option?.estimatedPayment">
        <p>{{ option?.estimatedPayment }}</p>
        <span>Per Month</span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  defineProps,
  withDefaults,
  reactive,
  ref,
  defineEmits,
  computed,
  watch,
  onMounted,
} from "vue";
import BaseFormFieldRadio from "@/components/v2/shared/BaseFormFieldRefiRadio.vue";
import { FieldSchema } from "@/models/form";
import { useStore } from "vuex";

const store = useStore();

interface Props {
  rateType: string;
  option: any;
  singleRateTypeAvailable?: boolean;
  singleRateOptionClicked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  singleRateTypeAvailable: false,
  singleRateOptionClicked: false,
});

const emit = defineEmits(["radioButtonClicked"]);

const ratePlanOption = ref<any | undefined>(undefined);

const selectedRatePlanId = computed(
  () => store.state.refiLoanConfigurationModule.selectedRatePlanId,
);
const selectedRateOption = computed(
  () => store.state.refiLoanConfigurationModule.selectedRateOption,
);

const isSelected = computed(() => ratePlanOption.value?.rateId === selectedRatePlanId.value);
const styleOption = computed(() => store.getters["refiLoanConfigurationModule/StyleOption"]);

function handleRadioButtonClick(event: any) {
  if (event) {
    store.dispatch("refiLoanConfigurationModule/setSelectedRatePlanId", event?.rateId);
    store.dispatch("refiLoanConfigurationModule/setSelectedRateOption", event);
    emit("radioButtonClicked", event);
  }
}
onMounted(() => {
  if (props.option && props.option.isSelected) {
    handleRadioButtonClick(props.option);
    ratePlanOption.value = props.option;
  }
});
const schema = reactive({
  paymentOption: {
    name: "paymentOption",
    type: "radio",
    required: false,
    hideLabel: true,
  } as FieldSchema,
});

watch(
  () => props.singleRateOptionClicked,
  (currentValue: boolean) => {
    if (currentValue) {
      ratePlanOption.value = undefined;

      handleRadioButtonClick(props.option);
      ratePlanOption.value = selectedRateOption.value;
    }
  },
);
</script>

<style lang="scss" scoped>
.hide {
  display: none;
}

hr.divider {
  transform: rotate(180deg);
  border: 1px solid var(--theme-color);
}

.paymentOption.active {
  .ratePercent,
  .paymentOptionHeader,
  .paymentOptionDetails,
  .monthlyPaymentAmount,
  .monthlyPaymentAmount p,
  .monthlyPaymentAmount span {
    font-weight: 600;
  }
}
.paymentOption {
  .ratePercent {
    padding-right: 1em;
    display: flex;
    align-items: center;
    font-family: var(--body-font);
    font-size: 18px;
    font-weight: 400;
    line-height: 21px;
    text-align: left;
  }
  .paymentOptionHeader {
    display: flex;
    flex-direction: row;
    font-weight: 400;
  }
  .paymentOptionDetails {
    display: flex;
    flex-direction: row;
    gap: 1em;
    padding-left: 32px;
  }
  .monthlyPaymentAmount {
    display: flex;
    flex-direction: column;
    font-weight: 400;
  }
  .monthlyPaymentAmount p {
    margin: 0;
    font-weight: 400;
  }
}
.paymentOption.single {
  .paymentOptionDetails.styleOption1 {
    padding-left: 32px;
  }
  .paymentOptionDetails.styleOption2 {
    padding-left: 0px;
  }
  .monthlyPaymentAmount {
    border-left: 1px solid var(--theme-color);
    padding-left: 1em;
    flex-direction: row;
    gap: 0.5em;
    align-items: center;
    font-family: var(--body-font);
    font-size: 18px;
    font-weight: 400;
    line-height: 21px;
    text-align: left;
  }
}

.rateType {
  vertical-align: middle;
  font-weight: 400;
  font-family: var(--body-font);
  font-size: 1.25rem;
  color: #333333;
}
</style>
